import type { HPOSuggestion } from '@/components/common/hpoTypes'
import { rankHpoResults, useHpoRemoteSearch } from '@/components/common/useHpoRemoteSearch'
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent, type RefObject } from 'react'
import type { ClinicalSummaryCategory } from './clinicalSummaryCategoryStyles'
import type { ClinicalSummaryPhenotypeAddAction } from './clinicalSummaryPhenotypeModel'

const MIN_QUERY_LENGTH = 3
const MAX_RESULTS = 15
/** Figma R15 / S08: the popover sits 14px under "Add phenotype", its right edge on the button's. */
const POPOVER_GAP = 14
const STATUS_ROW_CLASS = 'p-1.5 text-sm leading-5 text-text-subtle dark:text-gray-500'
const ACTION_BUTTON_CLASS =
  'rounded-full border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 px-2.5 py-1 text-xs font-medium leading-4 text-text-heading dark:text-white hover:bg-background-secondary-medium dark:hover:bg-gray-800 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-border-brand'

export type AddPhenotypePopoverCloseReason = 'escape' | 'outside'

interface ClinicalSummaryAddPhenotypePopoverProps {
  /** The button that toggles the popover: a press on it is not an outside press. */
  anchorRef: RefObject<HTMLElement | null>
  /** The model's add buttons, shown on every result that is not on the order yet. */
  addActions: readonly ClinicalSummaryPhenotypeAddAction[]
  /** HP codes already on the order: their results say so instead of offering an add. */
  onOrderCodes: ReadonlySet<string>
  onAdd: (term: HPOSuggestion, category: ClinicalSummaryCategory) => void
  onClose: (reason: AddPhenotypePopoverCloseReason) => void
}

/** Anchored under the Add phenotype button; laid out against the viewport, so no backdrop. */
export function ClinicalSummaryAddPhenotypePopover({
  anchorRef,
  addActions,
  onOrderCodes,
  onAdd,
  onClose,
}: ClinicalSummaryAddPhenotypePopoverProps) {
  const listId = useId()
  const rootRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState('')
  /**
   * The modal body scrolls, and an absolutely positioned overlay is clipped at that scroller's
   * edge however high its z-index, so the results were cut off. `fixed` is laid out against the
   * viewport and escapes the clip without moving the popover in the DOM, which keeps its focus
   * handling and outside-press check intact. Right-aligned, so the right edges are what line up.
   */
  const [viewportPosition, setViewportPosition] = useState<{ top: number; right: number } | null>(null)

  useLayoutEffect(() => {
    const anchor = anchorRef.current
    if (!anchor) return
    const place = () => {
      const box = anchor.getBoundingClientRect()
      setViewportPosition({ top: box.bottom + POPOVER_GAP, right: window.innerWidth - box.right })
    }
    place()
    const scroller = anchor.closest('.overflow-y-auto')
    scroller?.addEventListener('scroll', place, { passive: true })
    window.addEventListener('resize', place)
    return () => {
      scroller?.removeEventListener('scroll', place)
      window.removeEventListener('resize', place)
    }
  }, [anchorRef])
  /** -1 until the arrow keys pick a row; the pointer never moves it. */
  const [activeIndex, setActiveIndex] = useState(-1)
  const queryReady = query.trim().length >= MIN_QUERY_LENGTH
  const { results, isSearching, failed } = useHpoRemoteSearch(query, {
    minQueryLength: MIN_QUERY_LENGTH,
    limit: MAX_RESULTS,
  })
  const ranked = useMemo(() => rankHpoResults(query, results), [query, results])
  const onOrder = (term: HPOSuggestion) => onOrderCodes.has(term.hpoCode) || onOrderCodes.has(term.id)
  const optionId = (index: number) => `${listId}-option-${index}`
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    // focus() alone would centre the field in the editor's scroll area; scroll only as far as the popover needs.
    inputRef.current?.focus({ preventScroll: true })
    rootRef.current?.scrollIntoView?.({ block: 'nearest' })
  }, [])

  // Typing re-ranks before the debounced search returns, so the highlighted row would point at another term.
  useEffect(() => {
    setActiveIndex(-1)
  }, [ranked])

  useEffect(() => {
    if (activeIndex < 0) return
    document.getElementById(`${listId}-option-${activeIndex}`)?.scrollIntoView?.({ block: 'nearest' })
  }, [activeIndex, listId])

  useEffect(() => {
    // Capture on window runs before the editor modal's Escape (Save & Close) on document.
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      event.stopPropagation()
      onCloseRef.current('escape')
    }
    // No preventDefault: the press still reaches whatever it landed on.
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target
      if (!(target instanceof Node)) return
      if (rootRef.current?.contains(target) || anchorRef.current?.contains(target)) return
      onCloseRef.current('outside')
    }
    window.addEventListener('keydown', onKeyDown, true)
    document.addEventListener('pointerdown', onPointerDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown, true)
      document.removeEventListener('pointerdown', onPointerDown)
    }
  }, [anchorRef])

  const add = (term: HPOSuggestion, category: ClinicalSummaryCategory) => {
    onAdd(term, category)
    inputRef.current?.focus()
  }

  const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!queryReady || ranked.length === 0) return
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActiveIndex((index) => (index + 1) % ranked.length)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveIndex((index) => (index <= 0 ? ranked.length - 1 : index - 1))
    } else if (event.key === 'Enter') {
      event.preventDefault()
      const term = ranked[activeIndex]
      if (term && !onOrder(term) && addActions[0]) add(term, addActions[0].category)
    }
  }

  const showList = queryReady && ranked.length > 0
  let status: string | null = null
  if (queryReady && ranked.length === 0) {
    if (isSearching) status = 'Searching…'
    else if (failed) status = 'Search is unavailable right now. Try again in a moment.'
    else status = 'No matching phenotypes'
  }

  return (
    <div
      ref={rootRef}
      role="dialog"
      aria-label="Add phenotype"
      style={viewportPosition ?? undefined}
      className={`starting:opacity-0 z-30 flex w-[380px] flex-col rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 p-2.5 shadow-xl transition-opacity duration-[120ms] ease-out motion-reduce:transition-none ${
        viewportPosition ? 'fixed' : 'absolute right-0 top-[calc(100%+14px)]'
      }`}
    >
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-label="Search phenotypes"
        aria-autocomplete="list"
        aria-expanded={showList}
        aria-controls={listId}
        aria-activedescendant={showList && activeIndex >= 0 ? optionId(activeIndex) : undefined}
        autoComplete="off"
        spellCheck={false}
        placeholder="Search phenotypes"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={onInputKeyDown}
        className="w-full rounded-full border-[1.5px] border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 font-sans text-sm font-normal leading-5 text-text-heading dark:text-white [box-shadow:none] placeholder:text-text-subtle dark:placeholder:text-gray-500 focus:border-border-base dark:focus:border-gray-700 focus:outline-none"
      />
      {showList ? (
        <ul
          id={listId}
          role="listbox"
          aria-label="Phenotype results"
          className="mt-2 flex max-h-[294px] flex-col overflow-y-auto"
        >
          {ranked.map((term, index) => {
            const added = onOrder(term)
            const twoActions = !added && addActions.length > 1
            return (
              <li
                key={term.id}
                id={optionId(index)}
                role="option"
                aria-selected={index === activeIndex}
                aria-disabled={added || undefined}
                data-hpo-code={term.hpoCode}
                className={`flex items-center border-b border-border-base dark:border-gray-700 p-1.5 ${twoActions ? 'gap-1.5' : 'gap-2.5'} ${
                  index === activeIndex ? 'bg-background-secondary-medium dark:bg-gray-800' : ''
                }`}
              >
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-sm font-normal leading-5 text-text-heading dark:text-white">{term.name}</span>
                  <span className="text-xs font-medium leading-4 text-text-subtle dark:text-gray-500">{term.hpoCode}</span>
                </div>
                {added ? (
                  <span className="shrink-0 text-xs font-medium leading-5 text-text-fg-success dark:text-success-100">On the order</span>
                ) : (
                  <div className="flex shrink-0 items-center gap-1.5">
                    {addActions.map((action) => (
                      <button
                        key={action.label}
                        type="button"
                        aria-label={`${action.label} ${term.name}`}
                        onClick={() => add(term, action.category)}
                        className={ACTION_BUTTON_CLASS}
                      >
                        {action.label}
                      </button>
                    ))}
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      ) : null}
      {status ? (
        <p role="status" className={`mt-2 ${STATUS_ROW_CLASS}`}>
          {status}
        </p>
      ) : null}
    </div>
  )
}
